import type { WifiVoucherTicketCopy } from "../../theme/brand.config";
import { initPrinter, boldOn, boldOff, textLine, lineFeed, feedAndCut } from "./escpos";
import { wrapText } from "./ticket-layout";

/**
 * WiFi voucher ticket layout (T011, FR-305, contracts/peripheral-wifi-voucher.md).
 *
 * Composition only, sibling of `ticket-layout.ts` and reusing the same
 * ESC/POS primitives — no new byte-level encoder needed. `copy` carries every
 * word printed here; this file owns none of it (Constitution Principle VI).
 *
 * NO SALE, NO PRICE, NO TOTAL anywhere on this path (FR-305) — the code and
 * `copy` are the entire input, exactly as `renderTicket` never sees a price.
 */

export interface WifiVoucherLayoutOptions {
  /** Characters per printable line. Default matches 58mm paper. */
  charsPerLine?: number;
  /** Capability-detected by the caller — never assumed. */
  canCut?: boolean;
}

const DEFAULT_CHARS_PER_LINE = 32;
const FOOTER_FEED_LINES = 4;

function concat(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.length;
  }
  return out;
}

function wrappedLines(value: string, charsPerLine: number): Uint8Array[] {
  return wrapText(value, charsPerLine).map((line) => textLine(line));
}

/** Render one WiFi voucher into the full ESC/POS byte sequence. */
export function renderWifiVoucher(
  code: string,
  copy: WifiVoucherTicketCopy,
  options: WifiVoucherLayoutOptions = {},
): Uint8Array {
  const charsPerLine = options.charsPerLine ?? DEFAULT_CHARS_PER_LINE;
  const canCut = options.canCut ?? false;

  const chunks: Uint8Array[] = [initPrinter()];

  chunks.push(boldOn());
  chunks.push(...wrappedLines(copy.heading, charsPerLine));
  chunks.push(boldOff());
  chunks.push(lineFeed());

  chunks.push(boldOn(), textLine(code), boldOff());
  chunks.push(lineFeed());

  for (const instruction of copy.instructions) {
    chunks.push(...wrappedLines(instruction, charsPerLine));
  }

  chunks.push(lineFeed());
  chunks.push(...wrappedLines(copy.tagline, charsPerLine));

  chunks.push(feedAndCut(FOOTER_FEED_LINES, canCut));

  return concat(chunks);
}
